import {
  leadsListQueryKey,
  leadsPartialUpdateMutation,
  leadsRetrieveQueryKey,
  LeadsStatusesListData,
  leadsStatusesListInfiniteOptions,
  logsListQueryKey,
  PaginatedReturnLeadStatusListReadable,
  ReturnLeadReadable,
  ReturnLeadStatusReadable
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, Badge, Flex, Tooltip } from 'antd'
import { useState } from 'react'

import { CreateOpportunityModal } from './CreateOpportunityModal'
import { RefusalReasonModal } from './RefusalReasonModal'

type Props = {
  lead?: ReturnLeadReadable
  leadType?: number
  onSuccess?: () => void
}

type StatusOption = ReturnLeadStatusReadable & {
  requires_opportunity?: boolean
  requires_refusal_reason?: boolean
}

const StatusLabel = ({
  name,
  color
}: {
  name?: React.ReactNode
  color?: string
}) => {
  const { t } = useLocales()

  return (
    <Flex align="center" justify="left" gap={8} className="w-full">
      <Badge color={color} />

      <Text isEllipsis>{name || t('opportunity.without_status')}</Text>
    </Flex>
  )
}

export const ChangeFieldStatus = ({ lead, leadType, onSuccess }: Props) => {
  const { t } = useLocales()
  const { notification } = App.useApp()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const [pendingStatusId, setPendingStatusId] = useState<number | null>(null)
  const [refusalStatusId, setRefusalStatusId] = useState<number | null>(null)

  const { mutate, isPending } = useMutation({
    ...leadsPartialUpdateMutation(),
    onError: (error) => showApiErrors(error),
    onSuccess: () => {
      invalidateQueries([
        leadsListQueryKey(),
        leadsRetrieveQueryKey({ path: { id: Number(lead?.id) } }),
        logsListQueryKey()
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
      onSuccess?.()
    }
  })

  const changeStatus = (value: number) => {
    mutate({
      path: { id: lead?.id || 0 },
      body: { status: value }
    })
  }

  const onChange = (value: number | null, option: StatusOption) => {
    if (value === null) return

    if (option?.requires_refusal_reason) {
      setRefusalStatusId(value)
    } else if (option?.requires_opportunity) {
      setPendingStatusId(value)
    } else {
      changeStatus(value)
    }
  }

  return (
    <>
      <InfiniteSelect<
        LeadsStatusesListData,
        PaginatedReturnLeadStatusListReadable,
        ReturnLeadStatusReadable
      >
        getQueryFunction={leadsStatusesListInfiniteOptions}
        getOptions={({ options }) => {
          const allowedIds = lead?.status?.allowed_transition_statuses

          return options.map(
            ({
              name,
              id,
              color,
              requires_opportunity,
              requires_refusal_reason
            }) => {
              const isDisabled =
                allowedIds !== undefined &&
                allowedIds !== null &&
                !allowedIds.includes(id) &&
                id !== lead?.status?.id

              return {
                label: (
                  <Tooltip
                    title={isDisabled ? t('transition_not_allowed') : undefined}
                    placement="left"
                  >
                    <div style={{ pointerEvents: 'all' }}>
                      <StatusLabel name={name} color={color} />
                    </div>
                  </Tooltip>
                ),
                value: id,
                disabled: isDisabled,
                requires_opportunity,
                requires_refusal_reason
              }
            }
          )
        }}
        className="w-full"
        allowClear={false}
        showSearch={false}
        onSearch={undefined}
        baseOptions={{
          query: { lead_type: leadType }
        }}
        value={lead?.status?.id}
        onChange={onChange}
        disabled={isPending}
      />

      <RefusalReasonModal
        open={!!refusalStatusId}
        leadId={lead?.id}
        onConfirm={() => {
          if (refusalStatusId) changeStatus(refusalStatusId)
          setRefusalStatusId(null)
        }}
        onCancel={() => setRefusalStatusId(null)}
      />

      <CreateOpportunityModal
        open={!!pendingStatusId}
        leadId={lead?.id}
        onSuccess={() => {
          if (pendingStatusId) changeStatus(pendingStatusId)
          setPendingStatusId(null)
        }}
        onCancel={() => setPendingStatusId(null)}
      />
    </>
  )
}
